// Users and Roles: the admin pages and their dialogs. Role names and permission descriptions
// come from the API and are shown as sent.
import { plural } from "../../helpers";

const p = plural("en");

export default {
  list: {
    description: "Who can sign in to this host and with which role. Disabling a user or resetting their password ends their sessions at once.",
    add: "Add user",
    tableLabel: "Users",
    empty: "No users",
    emptyBody: "Add the first user with the button above.",
    cannotSee: "Your role cannot see users.",
    cannotSeeBody: "Ask an admin to add, reset or disable a user.",
    user: "User",
    role: "Role",
    added: "Added",
    password: "Password",
    status: "Status",
    enabled: "Enabled",
    invited: "Invited",
    active: "Active",
    disabled: "Disabled",
    roleOf: (email: string) => `Role of ${email}`,
    invitationLink: "Invitation link",
    invitationLinkFor: (email: string) => `Invitation link for ${email}`,
    issueCode: "Issue device code",
    issueCodeFor: (email: string) => `Issue device code for ${email}`,
    resetPassword: "Reset password",
    resetPasswordOf: (email: string) => `Reset the password of ${email}`,
    canSignIn: (email: string) => `${email} can sign in`,
    wasDisabled: (email: string) => `${email} disabled`,
    wasEnabled: (email: string) => `${email} enabled`,
    roleChanged: (email: string) => `${email}'s role changed`,
  },
  counts: {
    users: "Users",
    canSignIn: "Can sign in",
    invited: "Invited, no password yet",
    disabled: "Disabled",
  },
  dialogs: {
    notSaved: "Not saved",
    added: (email: string) => `${email} added`,
    addTitle: "Add a user",
    addSubmit: "Add user",
    email: "Email",
    displayName: "Display name",
    role: "Role",
    defaultRole: "User (default)",
    access: "How they get in",
    byInvitation: "An invitation link they open to choose a password (seven days, single use)",
    byPassword: "A temporary password they must change",
    temporaryPassword: "Temporary password",
    atLeast: (min: number) => `At least ${min} characters`,
    temporaryHelp: "Give it to them yourself; they choose their own at first sign-in.",
    resetTitle: (email: string) => `Reset the password of ${email}`,
    resetDone: (email: string) => `Password of ${email} reset; their sessions were signed out`,
    resetLead: "Every session of theirs ends and they must choose a new password at sign-in. Tell them this one yourself.",
    setPassword: "Set password",
    newPassword: "New password",
    codeTitle: (email: string) => `Device code for ${email}`,
    codeBody: (until: string) =>
      `Give it to them yourself; it is shown only now. They sign in on their phone (or a browser), open Devices and enter it to link their first device. It works once, until ${until}, and replaces any earlier code.`,
    oneTimeCode: "One-time device code",
    invitationTitle: (email: string) => `Invitation for ${email}`,
    invitationBody: (until: string) => `Share this link with them yourself; it is shown only now. It works once, until ${until}, and replaces any earlier link.`,
    invitationLink: "Invitation link",
    copyLink: "Copy link",
    linkCopied: "Link copied",
  },
  roles: {
    description: "One role per user. A role grants exactly the permissions ticked here; the API checks each one.",
    listLabel: "Roles",
    system: "system",
    everyPermission: "Every permission",
    permissions: (n: number) => p(n, { one: "# permission", other: "# permissions" }),
    members: (n: number) => p(n, { one: "# user", other: "# users" }),
    deleted: (name: string) => `${name} deleted`,
    createForm: "Create a role",
    newRole: "New role",
    newRoleName: "New role name",
    create: "Create role",
    holdsEvery: "Holds every permission",
    grouped: "Permissions, grouped by area",
    remove: "Delete role",
    assignTitle: "Give a user a role",
    assignDescription: "Their sessions keep working; the new permissions apply to their next request.",
    cannotSee: "Your role cannot see roles.",
    saved: (name: string) => `${name} saved`,
    superAdminNote: "The Super Admin always has every permission, including ones added later. It cannot be changed.",
    held: (count: number, total: number) => `${count} of ${total}`,
    save: "Save permissions",
    assigned: "Role assigned",
    assignForm: "Assign a role",
    user: "User",
    chooseUser: "Choose a user",
    role: "Role",
    chooseRole: "Choose a role",
    assign: "Assign",
  },
};
